// API Configuration
// Automatically switches between local and production based on environment

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8010';
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8010';
const ENV = process.env.REACT_APP_ENV || 'development';

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  WS_BASE_URL: WS_BASE_URL,
  ENV: ENV,
  IS_PRODUCTION: ENV === 'production',
  IS_DEVELOPMENT: ENV === 'development',
};

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/admin/login/',
    LOGOUT: '/admin/logout/',
    REFRESH_TOKEN: '/admin/refresh-token/',
    ME: '/admin/me/',
    FORGOT_PASSWORD: '/admin/forgot-password/',
    RESET_PASSWORD: '/admin/reset-password/',
    CHANGE_PASSWORD: '/admin/change-password/',
  },
  SUPERVISORS: {
    LIST:          '/admin/supervisor/list/',
    DETAIL:        (id) => `/admin/supervisor/${id}/`,
    CREATE:        '/admin/supervisor/create/',
    UPDATE:        (id) => `/admin/supervisor/update/${id}/`,
    DELETE:        (id) => `/admin/supervisor/delete/${id}/`,
    TOGGLE_ACTIVE: (id) => `/admin/supervisor/${id}/active/`,
    RESTORE:       (id) => `/admin/supervisor/${id}/restore/`,
    // One supervisor's full window — the drill-down + its Excel
    ANALYTICS:     (id) => `/admin/supervisor/${id}/analytics/`,
  },
  SALESPERSONS: {
    LIST:          '/admin/salesperson/list/',
    DETAIL:        (id) => `/admin/salesperson/${id}/`,
    CREATE:        '/admin/salesperson/create/',
    UPDATE:        (id) => `/admin/salesperson/update/${id}/`,
    DELETE:        (id) => `/admin/salesperson/delete/${id}/`,
    TOGGLE_ACTIVE: (id) => `/admin/salesperson/${id}/active/`,
    RESTORE:       (id) => `/admin/salesperson/${id}/restore/`,
    ANALYTICS:     (id) => `/admin/salesperson/${id}/analytics/`,
  },
  ANALYTICS: {
    SALES: '/admin/sales-analytics/',
    FUNNEL_LIST: '/admin/sales-analytics/inquiries/',
    BY_SOURCE: '/admin/sales/by-source/',
    BY_SOURCE_LIST: '/admin/sales/by-source/list/',
    DASHBOARD: '/admin/dashboard/',
    // Everything waiting for the admin right now — home queue + nav badges
    WORKLIST: '/admin/worklist/',
  },
  INQUIRIES: {
    LIST: '/admin/inquiries/',
    DETAIL: (id) => `/admin/inquiries/${id}/`,
    // What came in over a window, in full — customer, car, every part, the
    // salesperson who took it and every supervisor who priced it.
    DAY: '/admin/inquiries/day/',
    // The review chair acts, not just reads.
    FOLLOW_UPS: (id) => `/admin/inquiries/${id}/follow-ups/`,
    CLOSE: (id) => `/admin/inquiries/${id}/close/`,
  },
  SALE_REMINDERS: {
    LIST: '/admin/supervisor-sale-reminders/',
    DETAIL: (id) => `/admin/supervisor-sale-reminders/${id}/`,
  },
  // The whole direct-sales register: supervisor notes + the counter book
  SALE_NOTES: {
    LIST: '/admin/sale-notes/',
  },
  CUSTOMERS: {
    LIST: '/admin/customers/',
    DETAIL: (id) => `/admin/customers/${id}/`,
    // Excel-grade reports: every customer for a period, or one in full
    REPORT: '/admin/customers/report/',
    ONE_REPORT: (id) => `/admin/customers/${id}/report/`,
  },
  STOCKS: {
    LIST: '/admin/stocks/',
    DETAIL: (id) => `/admin/stocks/${id}/`,
    CAR_CATALOG: '/admin/stocks/car-catalog/',
    SALES: (id) => `/admin/stocks/${id}/sales/`,
    SALE: (id, saleId) => `/admin/stocks/${id}/sales/${saleId}/`,
    // Outside parts — batches of the SAME part bought up front and held on the
    // shelf (15 Seltos side mirrors), matched to inquiries automatically
    OUTSIDE_PARTS: '/admin/stocks/outside-parts/',
    OUTSIDE_PART: (id) => `/admin/stocks/outside-parts/${id}/`,
    OUTSIDE_PART_SALES: (id) => `/admin/stocks/outside-parts/${id}/sales/`,
    OUTSIDE_PART_SALE: (id, saleId) => `/admin/stocks/outside-parts/${id}/sales/${saleId}/`,
    // Trade-ins — old parts taken in on an exchange (resell / dismantle / scrap)
    TRADE_INS: '/admin/stocks/trade-ins/',
    TRADE_IN: (id) => `/admin/stocks/trade-ins/${id}/`,
    TRADE_IN_SALES: (id) => `/admin/stocks/trade-ins/${id}/sales/`,
    TRADE_IN_SALE: (id, saleId) => `/admin/stocks/trade-ins/${id}/sales/${saleId}/`,
  },
  // Money / Chit investments — money owed on a schedule (chit funds, private
  // borrows) tracked alongside stock inventory. Superadmin-gated, admin token.
  MONEY: {
    LIST: '/admin/stocks/money/',
    DETAIL: (id) => `/admin/stocks/money/${id}/`,
    PAY: (id) => `/admin/stocks/money/${id}/pay/`,
    PAYMENT: (id, paymentId) => `/admin/stocks/money/${id}/payments/${paymentId}/`,
  },
  // OSM purchases — vendor cost / advance / balance for outside-market parts
  OSM: {
    LIST: '/admin/osm/',
    SET: (partId) => `/admin/osm/${partId}/`,
    PAYMENTS: (partId) => `/admin/osm/${partId}/payments/`,
    PAYMENT: (partId, paymentId) => `/admin/osm/${partId}/payments/${paymentId}/`,
    // Couldn't procure — refund the customer (POST) / undo (DELETE)
    UNAVAILABLE: (partId) => `/admin/osm/${partId}/unavailable/`,
  },
  NOT_AVAILABLE: {
    LIST: '/admin/parts/not-available/',
    RESTOCK: '/admin/parts/restock/',
    RESTOCKS: '/admin/restocks/',
    RESTOCK_DETAIL: (id) => `/admin/restocks/${id}/`,
  },
  RETURNS: {
    RETURNABLE: '/admin/sales/returnable/',
    LIST: '/admin/returns/',
    DETAIL: (id) => `/admin/returns/${id}/`,
  },
  PAYMENTS: {
    LIST: '/admin/sales/payments/',
    SET: (inquiryId) => `/admin/sales/payments/${inquiryId}/`,
    // advance deals: mark the remaining balance collected (or undo)
    BALANCE: (inquiryId) => `/admin/sales/payments/${inquiryId}/balance/`,
  },
  SUPERVISOR_AUTH: {
    LOGIN: '/supervisor/login/',
  },
  // The supervisor phones themselves — a superadmin push telling every one of
  // them that a newer build is waiting on the Play Store. Tapping the notice
  // opens the store page.
  APP_UPDATE: {
    PUSH: '/admin/app-update/push/',
    // The versions the app is held to: latest, and the minimum that blocks
    RELEASE: '/admin/app-update/',
  },
  TEMPLATES: {
    LIST:   '/admin/templates/',
    DETAIL: (id) => `/admin/templates/${id}/`,
  },
  EXCHANGE_PARTS: {
    LIST:   '/admin/exchange-parts/',
    DETAIL: (id) => `/admin/exchange-parts/${id}/`,
  },
  // Superadmin audit — every price the admin corrected, with the reason
  PRICE_CHANGES: {
    LIST: '/admin/price-changes/',
  },
  // WHATSAPP: {
  //   CONFIG: '/admin/whatsapp-config/',
  // },
  EXPENSES: {
    LOGIN: '/expenses/login/',
    LOGOUT: '/expenses/logout/',
    LIST: '/expenses/',
    DETAIL: (id) => `/expenses/${id}/`,
    CATEGORIES: '/expenses/categories/',
    // Past titles + what the Money section still owes (chit/salary/rent)
    TITLE_SUGGESTIONS: '/expenses/title-suggestions/',
    // Read-only analytics — check any day/period, filter by category (superadmin)
    REPORT: '/expenses/report/',
    // Cash refunds owed to customers (from cash returns) — the night payout list
    CASH_REFUNDS: '/admin/cash-refunds/',
    CASH_REFUND_PAY: (id) => `/admin/cash-refunds/${id}/`,
    // Everything still to pay — refunds + OSM vendors + planned + chit
    PAYABLES: '/admin/ops/payables/',
  },
  // Planned / recurring payments (chit, salary, rent…) — Management section.
  // Served by the expenses app but usable with the main admin token.
  PLANNED: {
    LIST: '/expenses/planned/',
    DETAIL: (id) => `/expenses/planned/${id}/`,
    PAY: (id) => `/expenses/planned/${id}/pay/`,
  },
  // Dispatch — payment-confirmed orders waiting to be handed over.
  // Works with BOTH the superadmin token and the ops-admin (expenses) token.
  DISPATCH: {
    LIST: '/admin/dispatch/',
    SET: (id) => `/admin/dispatch/${id}/`,
  },
  // Orders overview — the whole funnel / pending delivery / delivered
  ORDERS: {
    LIST: '/admin/orders/',
    // One customer's full order history — the full-screen customer view
    CUSTOMER: (customerId) => `/admin/orders/customer/${customerId}/`,
    // Final approval — confirm payment + dispatch the courier-handed parts
    APPROVE: (id) => `/admin/orders/${id}/approve/`,
    // SUPERADMIN delete — removes the order after snapshotting it to the ledger
    DELETE: (id) => `/admin/orders/${id}/`,
  },
  // Deletion ledger — read-only audit of everything the superadmin removed
  DELETIONS: {
    LIST: '/admin/deletions/',
  },
  // Counter sales — walk-in entries + admin personal sales (same table)
  COUNTER: {
    SALES: '/admin/walkin/sales/',
    SALE: (id) => `/admin/walkin/sales/${id}/`,
    SALE_BALANCE: (id) => `/admin/walkin/sales/${id}/balance/`,
    // Payments checkpoint: approve a counter sale's payment
    SALE_CONFIRM: (id) => `/admin/walkin/sales/${id}/confirm/`,
    ANALYTICS: '/admin/walkin/analytics/',
    CHECK_CUSTOMER: '/admin/walkin/check-customer/',
    // Exchange line: old part received now / still pending
    ITEM_EXCHANGE: (id) => `/admin/walkin/items/${id}/exchange/`,
  },
  CARS: {
    // Light car catalog (brands→models + every known part name) — public,
    // the same source the salesperson inquiry form uses, so the Cars picker
    // offers exactly the cars the sales team sees.
    CATALOG: '/sales/cars/',

    // The WHOLE-CAR business: CAP's own cars, plus cars held for other owners
    // and sold on commission. Nothing to do with STOCKS.LIST — those are donor
    // cars bought to be cut up for parts.
    LIST: '/admin/cars/',
    DETAIL: (id) => `/admin/cars/${id}/`,
    SELL: (id) => `/admin/cars/${id}/sell/`,
    SALE: (saleId) => `/admin/cars/sales/${saleId}/`,
    EXPENSES: (id) => `/admin/cars/${id}/expenses/`,
    EXPENSE: (id, expenseId) => `/admin/cars/${id}/expenses/${expenseId}/`,
    SUMMARY: '/admin/cars/summary/',
    // CAP ↔ Cars float — money given to the car business, and what came back
    FUND_TRANSFERS: '/admin/cars/fund-transfers/',
    FUND_TRANSFER: (id) => `/admin/cars/fund-transfers/${id}/`,
    // The cars team's own logins
    STAFF: '/admin/cars/staff/',
    STAFF_DETAIL: (id) => `/admin/cars/staff/${id}/`,
    STAFF_RESTORE: (id) => `/admin/cars/staff/${id}/restore/`,
    STAFF_ACTIVE: (id) => `/admin/cars/staff/${id}/active/`,
  },
  // The CARS TEAM's own workspace — their login reaches these and nothing
  // else. Separate from CARS above, which is the superadmin's view of the
  // same business and needs the superadmin token.
  CARS_TEAM: {
    LOGIN: '/cars/login/',
    LOGOUT: '/cars/logout/',
    INVENTORY: '/cars/inventory/',
    CAR: (id) => `/cars/inventory/${id}/`,
    SELL: (id) => `/cars/inventory/${id}/sell/`,
    // The sale a car already has — correct it, collect the balance, or undo it
    SALE: (id) => `/cars/inventory/${id}/sale/`,
    // The buyer's money, one receipt at a time — token, loan, closing cash
    PAYMENTS: (id) => `/cars/inventory/${id}/payments/`,
    PAYMENT: (id, paymentId) => `/cars/inventory/${id}/payments/${paymentId}/`,
    // The handover — POST marks it (and builds the note), DELETE un-marks it
    DELIVER: (id) => `/cars/inventory/${id}/deliver/`,
    DELIVERY_NOTE: (id) => `/cars/inventory/${id}/delivery-note/`,
    // The token's receipt — GET the PDF, POST to host it for WhatsApp
    BOOKING_NOTE: (id) => `/cars/inventory/${id}/booking-note/`,
    INQUIRIES: '/cars/inquiries/',
    INQUIRY: (id) => `/cars/inquiries/${id}/`,
    SUMMARY: '/cars/summary/',
  },
  // Ops day-at-a-glance + the supervisor sale entry queue.
  // Works with BOTH the superadmin token and the ops-admin (expenses) token.
  OPS: {
    DAY: '/admin/ops/day/',
    SALE_REMINDERS: '/admin/supervisor-sale-reminders/',
    SALE_REMINDER: (id) => `/admin/supervisor-sale-reminders/${id}/`,
  },
};

export default API_CONFIG;
